import { useState } from "react";

import {
  makeGetApiCall,
  makePostApiCall,
  makePutApiCall,
} from "../../shared/makeApiCall";
import { SnackbarData, SnackbarType } from "../../components/Snackbar";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import { generateUniqueNumber } from "../../shared/utility";
import { set } from "date-fns";

export function CampaignDetailsViewModel(logout, campaignId, addSnackbar) {
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [campaignAnalytics, setCampaignAnalytics] = useState(null);
  const [reportInProgress, setReportInProgress] = useState(false);
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });

  const getCampaign = async function (id) {
    setLoading(true);
    try {
      const callResult = await makePostApiCall(`campaigns/${id}`, {});
      if (callResult.success) {
        setCampaign(callResult.data.campaign ?? callResult.data);
      } else if (callResult.errorStatus === 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const updateCampaign = async function (id, values) {
    const payload = {
      name: values.name,
      location: values.location,
      address: values.address,
      employee_name: values.employee_name,
    };
    const callResult = await makePutApiCall(`campaigns/${id}`, payload);
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Campaign updated successfully",
          SnackbarType.Success,
        ),
      );
      getCampaign(id);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      } else {
        showErrorModal(
          "Campaign Update Error",
          callResult.data?.message || "Could not update campaign",
        );
      }
    }
  };

  const getProductsForDropdown = async function (search, page) {
    const payload = { page: page || 1, search: search ?? null };
    const callResult = await makePostApiCall(`products`, payload);
    if (callResult.success) {
      return callResult.data;
    }
    if (callResult.errorStatus === 401) {
      logout();
    }
    return { data: [] };
  };

  const campaignDistributedAnalytics = async function (campaign_id) {
    const callResult = await makeGetApiCall(
      `campaignDistributedAnalytics/${campaign_id}`,
      {
        campaign_id: campaign_id,
      },
    );

    if (callResult.success) {
      setCampaignAnalytics(callResult.data);
      return callResult.data;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(
        "Campaign Analytics Error",
        callResult.data?.message || "Could not get campaign analytics",
      );
      return { data: [] };
    }
  };

  const generateCampaignReport = async function (campaignId) {
    setReportInProgress(true);
    try {
      const callResult = await makePostApiCall(
        `printCampaignReport/${campaignId}`,
        {},
      );
      if (callResult.success) {
        window.open(callResult.data.location, "_blank");
      } else {
        if (callResult.errorStatus === 401) {
          logout();
        } else {
          showErrorModal(
            "Report Generation Error",
            callResult.data?.message || "Could not generate campaign report",
          );
        }
      }
    } finally {
      setReportInProgress(false);
    }
  };

  const showErrorModal = (title, subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  };

  return {
    campaign,
    campaignAnalytics,
    campaignDistributedAnalytics,
    emptyModal,
    generateCampaignReport,
    getCampaign,
    getProductsForDropdown,
    loading,
    reportInProgress,
    setEmptyModelOpen,
    updateCampaign,
  };
}
